// helper function that will convert a raw HTML string -> DOM Nodes -> React JSX
import { parseHtmlToReact } from "app/static/frontend/imports/desktopHelperFunctions"

/** close the InlineListAdder when we click on any element outside of it */
export const handleClickOutside = (updateInlineAppearance, uniqueFlvId, inlineId) =>
    updateInlineAppearance(uniqueFlvId, inlineId, {
        listDropDownEnabled: false,
        priorityDropDownEnabled: false,
        stanceDropDownEnabled: false,
        issueDropDownEnabled: false,
        alignmentDropDownEnabled: false,
    })

/**
 * helper function that determines whether or not we want to manually parse backend-generated html
 * @param {Object} args
 * @param {string} args.lineData the raw text of the line
 * @param {boolean} args.isHtml whether or not the raw text contains html
 * @param {number} args.inlineId the id of the parent Inline
 * @param {boolean} args.isSearchify whether or not search is in Searchify mode
 *
 * @returns {string} the (potentially) formatted text
 */
export const renderText = ({ lineData, isHtml, inlineId, isSearchify }) => {
    // if the lineData is invalid, return an empty string.
    if (!lineData) {
        return ""
    }
    return isHtml ? parseHtmlToReact(lineData, inlineId, isSearchify) : lineData
}

/**
 * prevents the Inline href (used for right click functionality) from being fired on a profile segue
 * @param   {event} the event triggered by an onClick
 */
export const segueToProfile = (onPress, e) => {
    onPress()
    e.preventDefault()
}

export const checkObjectIsEmpty = (obj) =>
    obj.constructor === Object &&
    Object.keys(obj).length > 0 &&
    Object.values(obj).every(
        (x) => x !== null && x !== undefined && x !== "" && x.constructor === Array && x.length !== 0,
    )

export const onError = (e) => {
    const blankAvatar = "https://static.quorum.us/global/common/images/member-images/large/blank-avatar.jpg"
    if (e.target.src !== blankAvatar) {
        e.target.onerror = null
        e.target.src = blankAvatar
    }
}

/**
 * Pads a number less than 10 and greater than or equal to 0 with a 0
 * @param {int} timeNumber - the input number
 * @returns {string} The updated number
 */
export const timePad = (timeNumber) => {
    if (timeNumber <= 9 && timeNumber >= 0) {
        return `0${timeNumber}`
    }

    return timeNumber
}

/**
 * Finds a sequence number (from a DocumentType.committee_transcript object) given the currentTime
 * @param {int} currentTime - the current time in the Transcripts video
 * @returns {[newSequence: int, newTime: int]} The updated sequence/time tuple
 */
export const findSequenceFromTime = ({ currentTime, sequenceTimeCacheArray }) => {
    const [firstSequence, firstTime] = sequenceTimeCacheArray[0]
    const [lastSequence, lastTime] = sequenceTimeCacheArray[sequenceTimeCacheArray.length - 1]

    if (currentTime < firstTime) {
        return undefined
    }

    if (currentTime >= lastTime) {
        return [parseInt(lastSequence), lastTime]
    }

    const [newSequence, newTime] =
        sequenceTimeCacheArray[sequenceTimeCacheArray.findIndex(([sequence, time]) => time > currentTime)]

    return [parseInt(newSequence - 1), newTime]
}

/**
 * Get the offset for the request
 * @param {int} sequence - the sequence number corresponding to the current offset
 * @returns {int} The updated offset (finds the next divisor of OFFSET_STEP without remainder)
 */
export const nextLowestOffest = ({ offsetStep, sequence }) => {
    if (offsetStep === undefined) {
        return sequence
    }

    let number = parseInt(sequence)

    if (number >= 0) {
        while (number % offsetStep !== 0) {
            number -= 1
        }

        return number
    }

    return undefined
}

/**
 * Get search terms that should be appended to the href
 * @returns {string} The properly formatted uri query. ex. ?searchTerm=avocado%20AND%20school
 */
export const getSearchParams = () => {
    if (window.location.search) {
        // If there are multiple search terms, it would look something like : ?advanced_search=avocado+AND+school
        const hasMultipleSearchTerms = window.location.search.includes("AND") || window.location.search.includes("OR")

        // If multiple terms, then we need to format (replace + with " ", which gets encoded as '%20')
        // Then we replace 'advanced_search' with 'searchTerm'.
        const searchTerm = encodeURI(
            (hasMultipleSearchTerms ? window.location.search.replace(/\+/g, " ") : window.location.search).replace(
                /advanced_search/,
                "searchTerm",
            ),
        )

        return searchTerm
    }

    return ""
}

/**
 * Get the entire path for the anchor container's href
 * @param {boolean} isHighlightable
 * @param {string} href
 * @param {string} inlineId
 * @returns {string} ex. bill/1886661/?searchTerm=avocado%20AND%20school
 */
export const getInlineAnchorPath = ({ isHighlightable, href, inlineId }) =>
    !isHighlightable ? href && `${href}${getSearchParams()}` : `#${inlineId}`
